import { cloneDeep } from 'lodash-es';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ClientDetailsApi } from 'Api/Client/ClientDetailsApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { ExternalIntegrationsApi } from 'Api/ExternalIntegrations/ExternalIntegrationsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DueDiligenceWorkflow } from 'Components/DueDiligenceWorkflow/DueDiligenceWorkflow';
import { ExceptionsListing, ExceptionsListingProps } from 'Components/Exceptions/ExceptionsListing/ExceptionsListing';
import { FoldersTable, FoldersTableProps } from 'Components/Folders/FoldersTable/FoldersTable';
import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { IssuesListing, IssuesListingProps } from 'Components/Issues/IssuesListing/IssuesListing';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Modal } from 'Components/Modal/Modal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDashboard } from 'Components/PageLayout/PageLayoutDashboard';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ThirdPartyContactModal } from 'Components/TPRM/ThirdPartyContactModal/ThirdPartyContactModal';
import { ThirdPartyLocation } from 'Components/TPRM/ThirdPartyLocation/ThirdPartyLocation';
import { ThirdPartyMonitoring } from 'Components/TPRM/ThirdPartyMonitoring/ThirdPartyMonitoring';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { ArchivedDueDiligenceAssessmentsModal } from 'Components/UcrmTprm/ArchivedDueDiligenceAssessmentsModal/ArchivedDueDiligenceAssessmentsModal';
import { ArchivedDueDiligenceWorkflowsTable } from 'Components/UcrmTprm/ArchivedDueDiligenceWorkflowsTable/ArchivedDueDiligenceWorkflowsTable';
import { AssessmentsListing } from 'Components/UcrmTprm/AssessmentsListing/AssessmentsListing';
import { CommonAssessment } from 'Components/UcrmTprm/CommonAssessment/CommonAssessment';
import { CommonAssessmentSelectionModal, CommonAssessmentSelectionModalProps } from 'Components/UcrmTprm/CommonAssessmentSelectionModal/CommonAssessmentSelectionModal';
import { ConfirmUnlinkAssessmentSubjectsModal, ConfirmUnlinkAssessmentSubjectsModalProps } from 'Components/UcrmTprm/ConfirmUnlinkAssessmentSubjectsModal/ConfirmUnlinkAssessmentSubjectsModal';
import { DueDiligenceWorkflowDueDateModal } from 'Components/UcrmTprm/DueDiligenceWorkflowDueDateModal/DueDiligenceWorkflowDueDateModal';
import { DueDiligenceWorkflowFilesModal } from 'Components/UcrmTprm/DueDiligenceWorkflowFilesModal/DueDiligenceWorkflowFilesModal';
import { IrqTargetCompletionDateModal } from 'Components/UcrmTprm/IrqTargetCompletionDateModal/IrqTargetCompletionDateModal';
import { IrqTargetCompletionDateSelection, getDefaultIrqTargetCompletionDate, getIrqTargetCompletionDateSelectionProps } from 'Components/UcrmTprm/IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { UNAUTHORIZED_MESSAGE } from 'Config/Errors';
import { ICON_CLOSE, ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE, ICON_RESTART, ICON_SUBMIT } from 'Config/Icons';
import { TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, TPRM_UPDATE_THIRD_PARTY, TPRM_UPDATE_THIRD_PARTY_SERVICE } from 'Config/PathTemplates';
import { isForbiddenResponseError } from 'Helpers/Auth/ResponseUtil';
import { iso8601ToUsDateLong, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { useThirdPartyMonitoringMetricValues } from 'Hooks/ExternalIntegrations';
import { AssessmentWorkflowState, DueDiligenceWorkflowFileWithSource } from 'Models/DueDiligenceWorkflow';
import { ExceptionBreadcrumbSource, ExceptionResponse } from 'Models/Exceptions';
import { FolderBreadcrumbSource, FolderVersion } from 'Models/Folders';
import { InherentRiskQuestionnaireResponse, irqQuestionIsAnswered } from 'Models/InherentRisk';
import { IssueBreadcrumbSource, IssueResponse } from 'Models/Issues';
import { getEffectivenessVariantColor, numberAsEffectiveness, numberAsEffectivenessString } from 'Models/OperationalControls';
import { ActiveState } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating, getRiskRatingVariantColor, numberAsRiskRatingString, numberAsRiskScore, riskRatingAsString } from 'Models/RiskRating';
import { ThirdPartyContact, ThirdPartyResponse, ThirdPartyService, TprmDueDiligenceAssessment, TprmDueDiligenceWorkflowArchive } from 'Models/TPRM';
import { AssessmentsDisplayInfo } from 'Models/UcrmTprm';

import { InviteContactsModal } from './InviteContactsModal/InviteContactsModal';
import styles from './ServiceDashboard.module.css';

export const SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_ID = 'serviceId';
export const SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_NAME = 'serviceName';

export interface InherentRiskQuestionnaireProgress {
    numberOfQuestions: number;
    numberOfQuestionsAnswered: number;
}

enum PageElements {
    AssessmentHistoryModal,
    AssessmentFilesModal,
    ConfirmStartNewWorkflowModal,
    ConfirmRestartWorkflowModal,
    ConfirmClearWorkflowModal,
    CommonAssessmentSelectionModal,
    DelegatesModal,
    InviteServiceContactsModal,
    NoAssessmentsForInviteModal,
    NoValidContactsForInviteModal,
    SetAssessmentDueDate,
    SetIrqTargetCompletionDate,
    ThirdPartyContactModal,
    ThirdPartyServiceContactModal,
    UnlinkCommonAssessmentModal,
    None,
}

interface UrlParams {
    service_id: string;
    third_party_id: string;
}

export interface ServiceDashboardProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    tprmAssessmentApi: TprmAssessmentApi;
    tprmApi: ClientTPRMApi;
    issuesApi: IssuesApi;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    externalIntegrationsApi: ExternalIntegrationsApi;
    clientDetailsApi: ClientDetailsApi;
}

export const ServiceDashboard = (props: ServiceDashboardProps): JSX.Element => {
    const { service_id: serviceIdUrlParam, third_party_id: thirdPartyIdUrlParam } = useParams<keyof UrlParams>() as UrlParams;
    const location = useLocation();
    const navigate = useNavigate();

    const cachedData = useCachedData();
    const [thirdPartyMonitoringMetricValues, thirdPartyMonitoringMetricValuesError] = useThirdPartyMonitoringMetricValues(thirdPartyIdUrlParam, props.externalIntegrationsApi);

    const [assessments, setAssessments] = useState<TprmDueDiligenceAssessment[]>();
    const [folders, setFolders] = useState<FolderVersion[]>();
    const [folderTypes, setFolderTypes] = useState<string[]>();
    const [irqTargetCompletionInDays, setIrqTargetCompletionInDays] = useState<number | null>(); // Will contain `null` if the request for data has completed, but no data exists.
    const [irqProgress, setIrqProgress] = useState<InherentRiskQuestionnaireProgress | null>(); // Will contain `null` if the request for data has completed, but no data exists.
    const [serviceResponse, setServiceResponse] = useState<ThirdPartyService>();
    const [thirdPartyResponse, setThirdPartyResponse] = useState<ThirdPartyResponse>();
    const [workflowArchives, setWorkflowArchives] = useState<TprmDueDiligenceWorkflowArchive[]>();
    const [tprmAccessDenied, setTprmAccessDenied] = useState<boolean>();
    const [zeroStateText, setZeroStateText] = useState<string>();
    const [displayedElement, setDisplayedElement] = useState<PageElements>(PageElements.None);
    // Has a value after the user chooses to view all assessments from a specific historical assessment. Assessments are displayed in a modal.
    const [displayedWorkflowArchive, setDisplayedWorkflowArchive] = useState<{ workflowArchive: TprmDueDiligenceWorkflowArchive }>();
    // Has a value after the user chooses to view all files from a specific historical assessment. Files are displayed in a modal.
    const [displayedWorkflowArchiveFiles, setDisplayedWorkflowArchiveFiles] = useState<{ workflowArchive: TprmDueDiligenceWorkflowArchive; categorizedFiles: DueDiligenceWorkflowFileWithSource[] }>();
    const [selectedItem, setSelectedItem] = useState<IssueResponse | ExceptionResponse | ThirdPartyService>();
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date>();

    const assessmentsDisplayInfo: AssessmentsDisplayInfo<TprmDueDiligenceAssessment> | undefined = useMemo(() => {
        if (assessments === undefined || serviceResponse === undefined) {
            return undefined;
        }

        return serviceResponse.assessment_workflow_setup.common_assessment_parent ? { usingCommonAssessments: true, parentSummary: serviceResponse.assessment_workflow_setup.common_assessment_parent } : { usingCommonAssessments: false, assessments };
    }, [assessments, serviceResponse]);

    const getAssessments = useCallback(async (): Promise<void> => {
        try {
            const response = await props.tprmAssessmentApi.getAllAssessmentsForAssessmentSubject(thirdPartyIdUrlParam, serviceIdUrlParam);
            setAssessments(response.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.tprmAssessmentApi, thirdPartyIdUrlParam, serviceIdUrlParam]);

    const getInherentRiskQuestionnaireTargetCompletion = useCallback(async () => {
        try {
            const response = await props.dueDiligenceAssessmentConfigurationApi.getTimeframes();
            setIrqTargetCompletionInDays(response.data.expected_days_to_complete_irq ?? null);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.dueDiligenceAssessmentConfigurationApi]);

    const getServiceDetails = useCallback(async (): Promise<void> => {
        try {
            const detailedServiceResponse = await props.tprmApi.getServiceDetails(thirdPartyIdUrlParam, serviceIdUrlParam);
            setServiceResponse(detailedServiceResponse.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [serviceIdUrlParam, thirdPartyIdUrlParam, props.tprmApi]);

    const getThirdPartyDetails = useCallback(async (): Promise<void> => {
        try {
            const detailedThirdPartyResponse = await props.tprmApi.getThirdPartyDetails(thirdPartyIdUrlParam);
            setThirdPartyResponse(detailedThirdPartyResponse.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.tprmApi, thirdPartyIdUrlParam]);

    const getFolders = useCallback(async (): Promise<void> => {
        try {
            const response = await props.tprmApi.getAllCurrentFoldersForThirdParty(thirdPartyIdUrlParam);
            setFolders(response.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.tprmApi, thirdPartyIdUrlParam]);

    const getIrqProgress = useCallback(async (): Promise<void> => {
        try {
            const irqResponse = await props.tprmApi.getInherentRiskQuestionnaire(thirdPartyIdUrlParam, serviceIdUrlParam);
            setIrqProgress(irqResponse.data ? calculateInherentRiskQuestionnaireProgress(irqResponse.data) : null);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [serviceIdUrlParam, thirdPartyIdUrlParam, props.tprmApi]);

    /**
     * Initial network calls
     */
    useEffect(() => {
        getInherentRiskQuestionnaireTargetCompletion();
        getThirdPartyDetails();
        getServiceDetails();
        getIrqProgress();
        getAssessments();
        getFolders();
    }, [getInherentRiskQuestionnaireTargetCompletion, getServiceDetails, serviceIdUrlParam, thirdPartyIdUrlParam, props.tprmApi, getThirdPartyDetails, getIrqProgress, getAssessments, getFolders]);

    useEffect(() => {
        if (serviceResponse && serviceResponse.id !== serviceIdUrlParam) {
            /**
             * Reset state when service id params change.
             * This prop changes when a user clicks a link that redirects them to a different service dashboard.
             * React Router does not fully re-initialize the component. i.e. state is not re-initialized.
             * Resetting the values causes the placeholder animation to show while network calls are being made and state is being updated.
             * The client details and associated third party don't change when navigating between linked services. If a user navigates to a service associated with a different third party, the component is re-initialized.
             */
            setServiceResponse(undefined);
        }

        // Show confirm notification email modal when contact third-party service button clicked from a child service.
        if (location.state && location.state.showConfirmNotificationEmailModal) {
            // Prevent showing the modal every time the page is reloaded.
            navigate(location.pathname, { replace: true });

            setDisplayedElement(PageElements.InviteServiceContactsModal);
        }
    }, [location.pathname, location.state, navigate, serviceIdUrlParam, serviceResponse]);

    /** Fetches all past assessments for the service, to be displayed in the Past Assessments table. */
    useEffect(() => {
        const getWorkflowArchives = async (): Promise<void> => {
            try {
                const response = await props.tprmAssessmentApi.getAllDueDiligenceWorkflowArchivesForService(thirdPartyIdUrlParam, serviceIdUrlParam);
                setWorkflowArchives(response.data);
            } catch (error) {
                if (isForbiddenResponseError(error)) {
                    setTprmAccessDenied(true);
                } else {
                    handleRequestError(error);
                }
            }
        };

        getWorkflowArchives();
    }, [thirdPartyIdUrlParam, serviceIdUrlParam, props.tprmAssessmentApi]);

    /** Fetches all folder types, for use in the filter of the Folders table. */
    useEffect(() => {
        const getFolderTypes = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getFolderTypes();
                setFolderTypes(response.data.type_set);
            } catch (error) {
                if (isForbiddenResponseError(error)) {
                    setTprmAccessDenied(true);
                } else {
                    handleRequestError(error);
                }
            }
        };

        getFolderTypes();
    }, [props.tprmApi]);

    const startNewAssessmentWorkflow = async (): Promise<string> => {
        await props.tprmApi.startNewAssessmentWorkflow(thirdPartyIdUrlParam, serviceIdUrlParam, { due_diligence_workflow_due_date: serviceResponse?.assessment_workflow_setup.due_date, irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined });
        await getServiceDetails();
        await getIrqProgress();
        return 'Due diligence workflow started.';
    };

    const restartAssessmentWorkflow = async (): Promise<string> => {
        await props.tprmApi.restartAssessmentWorkflow(thirdPartyIdUrlParam, serviceIdUrlParam, { due_diligence_workflow_due_date: serviceResponse?.assessment_workflow_setup.due_date, irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined });
        await getServiceDetails();
        await getIrqProgress();
        await getAssessments();
        return 'Due diligence workflow restarted.';
    };

    const clearAssessmentWorkflow = async (): Promise<string> => {
        await props.tprmApi.clearAssessmentWorkflow(thirdPartyIdUrlParam, serviceIdUrlParam);
        await getServiceDetails();
        await getIrqProgress();
        await getAssessments();
        return 'Due diligence workflow cleared.';
    };

    const handleRequestError = (error: Error): void => setZeroStateText(error.message);

    const hideModal = () => {
        setDisplayedElement(PageElements.None);
        setSelectedItem(undefined);
    };

    const handleAssessmentsClicked = (workflowArchive: TprmDueDiligenceWorkflowArchive) => {
        setDisplayedWorkflowArchive({ workflowArchive });
        setDisplayedElement(PageElements.AssessmentHistoryModal);
    };

    const handleWorkflowArchiveFilesClicked = (workflowArchive: TprmDueDiligenceWorkflowArchive, categorizedFiles: DueDiligenceWorkflowFileWithSource[]) => {
        setDisplayedWorkflowArchiveFiles({ workflowArchive, categorizedFiles });
        setDisplayedElement(PageElements.AssessmentFilesModal);
    };

    const commonAssessmentSelectionModalProps = (service: ThirdPartyService): CommonAssessmentSelectionModalProps => {
        return {
            module: ProductModule.TPRM,
            moduleApi: props.tprmApi,
            assessmentSubject: service,
            hideModal: hideModal,
            onComplete: getServiceDetails,
        };
    };

    const confirmUnlinkServicesModalProps = (service: ThirdPartyService): ConfirmUnlinkAssessmentSubjectsModalProps => {
        return {
            moduleApi: props.tprmApi,
            assessmentSubject: service,
            hideModal: hideModal,
            onComplete: getServiceDetails,
        };
    };

    /** Determine which ThirdParty contact information to display, based on how many contacts there are and what information is available. */
    const renderThirdPartyContacts = (modalToOpen: PageElements.ThirdPartyContactModal | PageElements.ThirdPartyServiceContactModal, thirdPartyContacts: ThirdPartyContact[]): JSX.Element | JSX.Element[] => {
        // If there are no ThirdParty contacts, display a dash.
        if (thirdPartyContacts.length === 0) {
            return (
                <Text variant="Text2" color="white">
                    -
                </Text>
            );
        }

        // If there are is one ThirdParty contact, display all of its available contact information.
        else if (thirdPartyContacts.length === 1) {
            const { name, email_address, phone_number } = thirdPartyContacts[0];
            return (
                <div className={styles.thirdPartyContactInformation}>
                    {name && (
                        <>
                            <Text color="white" variant="Text2" noStyles>
                                {name}
                            </Text>
                            <br />
                        </>
                    )}
                    {email_address && (
                        <>
                            <Text color="white" variant="Text2" noStyles>
                                {email_address}
                            </Text>
                            <br />
                        </>
                    )}
                    {phone_number && (
                        <>
                            <Text color="white" variant="Text2" noStyles>
                                {phone_number}
                            </Text>
                            <br />
                        </>
                    )}
                </div>
            );
        }

        // If there is more than one ThirdParty contact, display a link to a modal that contains all the contacts' information.
        else {
            return (
                <Button variant="linkText" size="lg" onClick={() => setDisplayedElement(modalToOpen)}>
                    {`Show ${thirdPartyContacts.length} Contacts`}
                </Button>
            );
        }
    };

    /** Determine which internal contact information to display, based on how many delegates there are. */
    const renderInternalServiceDelegates = (): JSX.Element | JSX.Element[] => {
        // If there are no delegates, display a dash.
        if (!serviceResponse || serviceResponse.delegates.length === 0) {
            return (
                <Text variant="Text2" color="white">
                    -
                </Text>
            );
        }

        // If there are is one delegate, display their name.
        else if (serviceResponse.delegates.length === 1) {
            return (
                <Text variant="Text2" color="white">
                    {getUserNameFromSubject(serviceResponse.delegates[0], cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}
                </Text>
            );
        }

        // If there is more than one delegate, display a link to a modal that contains all the delegates' info.
        else {
            return (
                <Button variant="linkText" size="lg" onClick={() => setDisplayedElement(PageElements.DelegatesModal)}>
                    {`Show ${serviceResponse.delegates.length} Delegates`}
                </Button>
            );
        }
    };

    const hasValidContactsForInvite = (service: ThirdPartyService): boolean => {
        return service.vendor_contacts.some((contact) => contact.email_address !== undefined);
    };

    /** Renders the contents of the ThirdParty/ThirdParty Service information dashlet. */
    const renderThirdPartyAndThirdPartyServiceInformationDashlet = (thirdParty: ThirdPartyResponse, thirdPartyService: ThirdPartyService): JSX.Element => {
        return (
            <div className={styles.smallSection}>
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Service Description
                </Text>
                <Text variant="Text2" color="white">
                    {thirdPartyService.description}
                </Text>
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Location
                </Text>
                <ThirdPartyLocation thirdPartyMetadata={thirdParty} variant="dashboard" />
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Website
                </Text>
                {thirdParty.website ? (
                    <Link openInNewTab size="lg" to={thirdParty.website}>
                        {thirdParty.website}
                    </Link>
                ) : (
                    <Text variant="Text2" color="white">
                        -
                    </Text>
                )}
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Shared Due Diligence Assessments
                </Text>
                <CommonAssessment assessmentSubject={thirdPartyService} module={ProductModule.TPRM} onSelectLinkToParent={onSelectLinkToParent} onSelectUnlinkFromParent={onSelectUnlinkFromParent} />
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Tags
                </Text>
                {thirdParty.tags.length > 0 ? (
                    <TagListing tags={cachedData.convertTagIdsToDisplayableTags(thirdParty.tags)} />
                ) : (
                    <Text variant="Text2" color="white">
                        -
                    </Text>
                )}
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Service Tags
                </Text>
                {thirdPartyService.tags.length > 0 ? (
                    <TagListing tags={cachedData.convertTagIdsToDisplayableTags(thirdPartyService.tags)} />
                ) : (
                    <Text variant="Text2" color="white">
                        -
                    </Text>
                )}
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Third-Party Service Responsible Organization
                </Text>
                <Text variant="Text2" color="white">
                    {thirdPartyService.responsible_organization ?? '-'}
                </Text>
            </div>
        );
    };

    /**
     * If Third-Party Risk Scoring is available, render it.
     * If an error was returned when querying Third-Party Risk Scoring data, show the error.
     * If neither, show nothing.
     */
    const renderThirdPartyMonitoringDashlet = () => {
        if (thirdPartyMonitoringMetricValues && thirdPartyMonitoringMetricValues.length > 0) {
            return (
                <>
                    <Text variant="Header2" color="white">
                        Third-Party Risk Scoring Information
                    </Text>
                    <ThirdPartyMonitoring thirdPartyRiskScores={thirdPartyMonitoringMetricValues} />
                </>
            );
        } else if (thirdPartyMonitoringMetricValuesError) {
            return (
                <>
                    <Text variant="Header2" color="white">
                        Third-Party Risk Scoring Information
                    </Text>
                    <Text color="white">{thirdPartyMonitoringMetricValuesError.message}</Text>
                </>
            );
        }
    };

    if (tprmAccessDenied) {
        return <Text>{UNAUTHORIZED_MESSAGE}</Text>;
    }

    if (zeroStateText) {
        return <Text>{zeroStateText}</Text>;
    }

    // The service ids are compared to prevent any stale data being shown when the service id parameter changes.
    if (!(serviceResponse && serviceResponse.id === serviceIdUrlParam && thirdPartyResponse && workflowArchives && folders && folderTypes && irqProgress !== undefined && irqTargetCompletionInDays !== undefined && assessmentsDisplayInfo !== undefined)) {
        return <Placeholder />;
    }

    const populateRecommendedIrqTargetCompletionDateAsDefault = () =>
        setSelectedIrqTargetCompletionDate(
            getDefaultIrqTargetCompletionDate({
                action: 'startServiceAssessmentWorkflow',
                currentAssessmentWorkflowIrqTargetCompletionDate: serviceResponse.assessment_workflow_setup.irq_target_completion_date,
                expectedIrqDurationInDays: irqTargetCompletionInDays ?? undefined,
            })
        );

    const onSelectLinkToParent = () => {
        setSelectedItem(cloneDeep(serviceResponse));
        setDisplayedElement(PageElements.CommonAssessmentSelectionModal);
    };

    const onSelectUnlinkFromParent = () => {
        setSelectedItem(cloneDeep(serviceResponse));
        setDisplayedElement(PageElements.UnlinkCommonAssessmentModal);
    };

    const inherent_risk_score = serviceResponse.inherent_risk_score ?? 0;
    const thirdPartyServiceTitle = `${serviceResponse.vendor_name} - ${serviceResponse.name}${serviceResponse.active_state === ActiveState.INACTIVE ? ' (Inactive)' : ''}`;
    const assessmentDueDateText = serviceResponse.assessment_workflow_setup.due_date ? iso8601ToUsDateLong(serviceResponse.assessment_workflow_setup.due_date) : '-';
    const lastAssessedDateText = serviceResponse.last_assessed ? iso8601ToUsDateLong(serviceResponse.last_assessed) : '-';
    const irqTargetCompletionDateText = serviceResponse.assessment_workflow_setup.irq_target_completion_date ? iso8601ToUsDateLong(serviceResponse.assessment_workflow_setup.irq_target_completion_date) : '-';
    const inherentRisk = numberAsRiskRatingString(inherent_risk_score);
    const inherentRiskVariant = getRiskRatingVariantColor(numberAsRiskScore(inherent_risk_score));
    const residualRisk = serviceResponse.residual_risk_score ? numberAsRiskRatingString(serviceResponse.residual_risk_score) : undefined;
    const residualRiskVariant = serviceResponse.residual_risk_score ? getRiskRatingVariantColor(numberAsRiskScore(serviceResponse.residual_risk_score)) : undefined;
    const thirdPartyManager = getUserNameFromSubject(thirdPartyResponse.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)!;
    const serviceManager = getUserNameFromSubject(serviceResponse.vendor_service_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)!;

    const issues = thirdPartyResponse.issues;
    const exceptions = thirdPartyResponse.exceptions;

    const issuesListingProps: IssuesListingProps = {
        type: ProductModule.TPRM,
        issues: issues,
        documentApi: props.documentApi,
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createIssueOrigin: {
            source: IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.TPRM,
            organizationId: thirdPartyResponse.id,
            assessmentSubjectId: serviceResponse.id,
            assessmentSubjectDisplayName: `${serviceResponse.vendor_name} - ${serviceResponse.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: thirdPartyResponse.id,
        preselectedOrganizationIdForFilter: thirdPartyResponse.id,
        organizations: [thirdPartyResponse],
    };

    const exceptionsListingProps: ExceptionsListingProps = {
        type: ProductModule.TPRM,
        exceptions: exceptions,
        documentApi: props.documentApi,
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createExceptionOrigin: {
            source: ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.TPRM,
            organizationId: thirdPartyResponse.id,
            assessmentSubjectId: serviceResponse.id,
            assessmentSubjectDisplayName: `${serviceResponse.vendor_name} - ${serviceResponse.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: thirdPartyResponse.id,
        preselectedOrganizationIdForFilter: thirdPartyResponse.id,
        organizations: [thirdPartyResponse],
    };

    const foldersListingProps: FoldersTableProps = {
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createFolderOrigin: {
            source: FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.TPRM,
            organizationId: thirdPartyResponse.id,
            assessmentSubjectId: serviceResponse.id,
            assessmentSubjectDisplayName: `${serviceResponse.vendor_name} - ${serviceResponse.name}`,
        },
        documentApi: props.documentApi,
        folders: folders,
        folderTypes: folderTypes,
        hideOrganizationInfo: true,
        module: ProductModule.TPRM,
        showTitleAndCreateButton: true,
        preselectedOrganizationIdForCreate: thirdPartyResponse.id,
        organizations: [thirdPartyResponse],
    };

    const irqTargetCompletionDatePicker = (
        <IrqTargetCompletionDateSelection
            {...getIrqTargetCompletionDateSelectionProps(
                ProductModule.TPRM,
                {
                    action: 'startServiceAssessmentWorkflow',
                    currentAssessmentWorkflowIrqTargetCompletionDate: serviceResponse.assessment_workflow_setup.irq_target_completion_date,
                    expectedIrqDurationInDays: irqTargetCompletionInDays ?? undefined,
                },
                setSelectedIrqTargetCompletionDate
            )}
        />
    );

    return (
        <>
            {displayedElement === PageElements.AssessmentHistoryModal && displayedWorkflowArchive && (
                <ArchivedDueDiligenceAssessmentsModal
                    workflowArchive={displayedWorkflowArchive.workflowArchive}
                    module={ProductModule.TPRM}
                    hideModal={() => {
                        setDisplayedElement(PageElements.None);
                        setDisplayedWorkflowArchive(undefined);
                    }}
                />
            )}
            {displayedElement === PageElements.AssessmentFilesModal && displayedWorkflowArchiveFiles && (
                <DueDiligenceWorkflowFilesModal
                    workflowArchive={displayedWorkflowArchiveFiles.workflowArchive}
                    categorizedFiles={displayedWorkflowArchiveFiles.categorizedFiles}
                    documentApi={props.documentApi}
                    hideModal={() => {
                        setDisplayedElement(PageElements.None);
                        setDisplayedWorkflowArchiveFiles(undefined);
                    }}
                />
            )}
            {displayedElement === PageElements.ConfirmStartNewWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Start New Due Diligence Workflow" areYouSureText="Are you sure you want to start the third-party due diligence workflow?" buttonText="START" buttonLoadingText="Starting..." buttonIcon={ICON_SUBMIT} performOperation={startNewAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>This will generate a new inherent risk questionnaire based on the latest configuration.</Text>
                    {irqTargetCompletionDatePicker}
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.ConfirmRestartWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Restart Due Diligence Workflow" areYouSureText="Are you sure you want to restart the due diligence workflow?" buttonText="RESTART" buttonLoadingText="Restarting..." buttonIcon={ICON_RESTART} performOperation={restartAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>This will return the workflow to the first step (evaluating inherent risk), and the latest configuration will be applied to the inherent risk questionnaire. Third-party service contacts will lose access to questionnaires.</Text>
                    {irqTargetCompletionDatePicker}
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.ConfirmClearWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DESTRUCTIVE} headerText="Clear Due Diligence Workflow" areYouSureText="Are you sure you want to clear the due diligence workflow?" buttonText="CLEAR" buttonLoadingText="Clearing..." buttonIcon={ICON_DELETE_REMOVE} performOperation={clearAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>All responses to the inherent risk questionnaire, due diligence questionnaires, due diligence assessments, and final review will be lost. The inherent risk rating, overall control effectiveness, and residual risk rating for the service will be cleared.</Text>
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.CommonAssessmentSelectionModal && <CommonAssessmentSelectionModal {...commonAssessmentSelectionModalProps(selectedItem as ThirdPartyService)} />}
            {displayedElement === PageElements.UnlinkCommonAssessmentModal && <ConfirmUnlinkAssessmentSubjectsModal {...confirmUnlinkServicesModalProps(selectedItem as ThirdPartyService)} />}
            {displayedElement === PageElements.DelegatesModal && <UsersModal userIds={serviceResponse.delegates} header="Service Delegates" hideModal={hideModal} />}
            {displayedElement === PageElements.InviteServiceContactsModal && <InviteContactsModal hideModal={hideModal} service={serviceResponse} tprmAssessmentApi={props.tprmAssessmentApi} />}
            {displayedElement === PageElements.NoAssessmentsForInviteModal && (
                <Modal open header="Cannot Invite Third-party Service Contacts">
                    <Text>No assessments have been generated. Third-party service contacts cannot be invited.</Text>
                    <div className={styles.buttonContainer}>
                        <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                    </div>
                </Modal>
            )}
            {displayedElement === PageElements.NoValidContactsForInviteModal && (
                <Modal open header="Cannot Invite Third-party Service Contacts">
                    <Text>No third-party service contacts have a valid email address. Add or update contacts before inviting them.</Text>
                    <div className={styles.buttonContainer}>
                        <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                    </div>
                </Modal>
            )}
            {displayedElement === PageElements.SetAssessmentDueDate && <DueDiligenceWorkflowDueDateModal module={ProductModule.TPRM} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} assessmentSubject={serviceResponse} hideModal={hideModal} moduleApi={props.tprmApi} onDueDateSet={getServiceDetails} />}
            {displayedElement === PageElements.SetIrqTargetCompletionDate && <IrqTargetCompletionDateModal module={ProductModule.TPRM} hideModal={hideModal} assessmentSubject={serviceResponse} targetDateSet={getServiceDetails} moduleApi={props.tprmApi} />}
            {displayedElement === PageElements.ThirdPartyContactModal && <ThirdPartyContactModal header="Third-Party Contacts" hideModal={hideModal} thirdPartyContacts={thirdPartyResponse.vendor_contacts} />}
            {displayedElement === PageElements.ThirdPartyServiceContactModal && <ThirdPartyContactModal header="Third-Party Service Contacts" hideModal={hideModal} thirdPartyContacts={serviceResponse.vendor_contacts} />}

            <PageLayoutDashboard
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>{ProductModule.TPRM}</BreadcrumbLink>
                        <BreadcrumbText>{thirdPartyServiceTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={thirdPartyServiceTitle}
                headerButtons={
                    <>
                        <Link variant="primaryButton" to={generatePath(TPRM_UPDATE_THIRD_PARTY, { thirdPartyId: serviceResponse.vendor_id }) + `?${SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_ID}=${serviceResponse.id}&${SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_NAME}=${encodeURIComponent(serviceResponse.name)}`} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Third Party
                        </Link>
                        <Link variant="primaryButton" to={generatePath(TPRM_UPDATE_THIRD_PARTY_SERVICE, { thirdPartyId: serviceResponse.vendor_id, serviceId: serviceResponse.id }) + `?${SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_ID}=${serviceResponse.id}&${SEARCH_PARAM_SERVICE_DASHBOARD_ORIGIN_SERVICE_NAME}=${encodeURIComponent(serviceResponse.name)}`} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Third-Party Service
                        </Link>
                    </>
                }
                dashlets={[
                    {
                        dashlets: [
                            {
                                title: 'Third Party and Service Details',
                                content: renderThirdPartyAndThirdPartyServiceInformationDashlet(thirdPartyResponse, serviceResponse),
                            },
                            {
                                title: 'Due Diligence Workflow Summary',
                                content: (
                                    <div className={styles.riskAndEffectiveness}>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Inherent Risk
                                            </Text>
                                            {inherentRisk && inherentRisk !== riskRatingAsString(RiskRating.UNRATED) ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={inherentRiskVariant} />
                                                    <Text variant="Text2" color="white">
                                                        {inherentRisk}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Overall Control Effectiveness
                                            </Text>
                                            {serviceResponse.control_effectiveness ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(serviceResponse.control_effectiveness))} />
                                                    <Text variant="Text2" color="white">
                                                        {numberAsEffectivenessString(serviceResponse.control_effectiveness)}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Residual Risk
                                            </Text>
                                            {residualRisk && residualRiskVariant ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={residualRiskVariant} />
                                                    <Text variant="Text2" color="white">
                                                        {residualRisk}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Inherent Risk Questionnaire Due Date
                                            </Text>
                                            <div className={styles.textAboveLinkButton}>
                                                <Text variant="Text2" color="white">
                                                    {irqTargetCompletionDateText}
                                                </Text>
                                            </div>
                                            <Button variant="linkText" onClick={() => setDisplayedElement(PageElements.SetIrqTargetCompletionDate)} size="lg">
                                                {serviceResponse.assessment_workflow_setup.irq_target_completion_date ? 'Edit Inherent Risk Questionnaire Due Date' : 'Set Inherent Risk Questionnaire Due Date'}
                                            </Button>
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Due Diligence Workflow Due Date
                                            </Text>
                                            <div className={styles.textAboveLinkButton}>
                                                <Text variant="Text2" color="white">
                                                    {assessmentDueDateText}
                                                </Text>
                                            </div>
                                            <Button variant="linkText" onClick={() => setDisplayedElement(PageElements.SetAssessmentDueDate)} size="lg">
                                                {serviceResponse.assessment_workflow_setup.due_date ? 'Edit Due Diligence Workflow Due Date' : 'Set Due Diligence Workflow Due Date'}
                                            </Button>
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Last Completed
                                            </Text>
                                            <Text variant="Text2" color="white">
                                                {lastAssessedDateText}
                                            </Text>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                dashlets: [
                                    {
                                        title: 'Internal Contacts',
                                        content: (
                                            <>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Third-Party Manager
                                                </Text>
                                                <Text variant="Text2" color="white">
                                                    {thirdPartyManager}
                                                </Text>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Service Manager
                                                </Text>
                                                <Text variant="Text2" color="white">
                                                    {serviceManager}
                                                </Text>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Service Delegates
                                                </Text>
                                                {renderInternalServiceDelegates()}
                                            </>
                                        ),
                                    },
                                    {
                                        title: 'External Contacts',
                                        content: (
                                            <>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Third-Party Contact Information
                                                </Text>
                                                {renderThirdPartyContacts(PageElements.ThirdPartyContactModal, thirdPartyResponse.vendor_contacts)}
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Service Contact Information
                                                </Text>
                                                {renderThirdPartyContacts(PageElements.ThirdPartyServiceContactModal, serviceResponse.vendor_contacts)}
                                            </>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content:
                            serviceResponse.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT ? (
                                <>
                                    <Text variant="Header2" color="white">
                                        Due Diligence Workflow
                                    </Text>
                                    <Button
                                        variant="linkText"
                                        onClick={() => {
                                            populateRecommendedIrqTargetCompletionDateAsDefault();
                                            setDisplayedElement(PageElements.ConfirmStartNewWorkflowModal);
                                        }}
                                        size="sm"
                                    >
                                        Begin new due diligence workflow
                                    </Button>
                                </>
                            ) : (
                                <DueDiligenceWorkflow
                                    assessmentsDisplayInfo={assessmentsDisplayInfo}
                                    module={ProductModule.TPRM}
                                    onSelectClearWorkflow={() => setDisplayedElement(PageElements.ConfirmClearWorkflowModal)}
                                    onSelectInviteServiceContacts={() => {
                                        if (!assessments || assessments.length === 0) {
                                            setDisplayedElement(PageElements.NoAssessmentsForInviteModal);
                                        } else if (!hasValidContactsForInvite(serviceResponse)) {
                                            setDisplayedElement(PageElements.NoValidContactsForInviteModal);
                                        } else {
                                            setDisplayedElement(PageElements.InviteServiceContactsModal);
                                        }
                                    }}
                                    onSelectRestartWorkflow={() => {
                                        populateRecommendedIrqTargetCompletionDateAsDefault();
                                        setDisplayedElement(PageElements.ConfirmRestartWorkflowModal);
                                    }}
                                    assessmentSubject={serviceResponse}
                                    assessmentApi={props.tprmAssessmentApi}
                                />
                            ),
                    },
                    ...(serviceResponse.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE
                        ? assessmentsDisplayInfo.usingCommonAssessments
                            ? [
                                  {
                                      title: 'Due Diligence Assessments',
                                      content: (
                                          <>
                                              <Text color="white">This third-party service is using the shared due diligence assessments provided by:</Text>
                                              <div>
                                                  <Link to={generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: assessmentsDisplayInfo.parentSummary.vendor_id, service_id: assessmentsDisplayInfo.parentSummary.id })} size="lg">
                                                      {assessmentsDisplayInfo.parentSummary.name}
                                                  </Link>
                                              </div>
                                          </>
                                      ),
                                  },
                              ]
                            : [
                                  {
                                      title: 'Due Diligence Assessments',
                                      content: <AssessmentsListing assessments={assessmentsDisplayInfo.assessments} module={ProductModule.TPRM} pageVariant="dashboard" />,
                                  },
                              ]
                        : []),
                    ...(thirdPartyMonitoringMetricValues && thirdPartyMonitoringMetricValues.length > 0
                        ? [
                              {
                                  content: renderThirdPartyMonitoringDashlet(),
                              },
                          ]
                        : []),
                    {
                        content: <IssuesListing {...issuesListingProps} />,
                    },
                    {
                        content: <ExceptionsListing {...exceptionsListingProps} />,
                    },
                    {
                        content: <FoldersTable {...foldersListingProps} />,
                    },
                    {
                        title: 'Due Diligence Workflow History',
                        content: (
                            <div data-testid="workflowArchivesTable">
                                <ArchivedDueDiligenceWorkflowsTable workflowArchives={workflowArchives} documentApi={props.documentApi} module={ProductModule.TPRM} onAssessmentsClicked={handleAssessmentsClicked} onFilesClicked={handleWorkflowArchiveFilesClicked} />
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
};

const calculateInherentRiskQuestionnaireProgress = (irq: InherentRiskQuestionnaireResponse): InherentRiskQuestionnaireProgress => {
    let numberOfQuestions = 0;
    let numberOfQuestionsAnswered = 0;

    irq.sections.forEach((section) => {
        if (!section.is_applicable) {
            numberOfQuestions += section.questions.length;
            numberOfQuestionsAnswered += section.questions.length;
        } else {
            section.questions.forEach((question) => {
                numberOfQuestions++;
                if (irqQuestionIsAnswered(question)) {
                    numberOfQuestionsAnswered++;
                }
            });
        }
    });

    return {
        numberOfQuestions,
        numberOfQuestionsAnswered,
    };
};
